import { Button, Input, Modal } from '../../common/_custom';
import CircularSelect from '../../common/MagicUpload/components/CircularSelect/CircularSelect';
import CountryCodesDropdown from '../../modules/Company/CompanyLogin/CountryCodesDropdown/CountryCodesDropdown';
import { DIRECTOR_DESIGNATIONS } from './constants';
import useAddDirectorModalLogic from './useAddDirectorModalLogic';
import { AddDirectorModalProps } from './types';
import styles from './AddDirectorModal.module.scss';
import { PAN_PROFILE_COPY } from '../ManagementProfile/managementProfileFields';
import LoadingSpinner from '../_custom/LoadingSpinner/LoadingSpinner';
import { isIndividualPan } from '../../utils/validatePAN';

function AddDirectorModal({ open, onClose, onSuccess, organizationId, panProfileFetching = false }: AddDirectorModalProps) {
    const { form, errors, saving, setField, handleClose, handleSave, getLabelStyle, fieldFocusHandlers, setPhoneCountryCode } =
        useAddDirectorModalLogic({ open, onClose, onSuccess, organizationId });

    // The director is already created once the enrichment is fetching — the form is only still up
    // to show it happen, so Add must not be live enough to create a second one.
    const submitBlocked = saving || panProfileFetching;

    return (
        <Modal open={open} onClose={handleClose} size="flexible" persistent>
            <div className={styles.AddDirectorModal}>
                <div className={styles.Header}>
                    <h3 className={styles.Heading}>Add New</h3>
                </div>
                <div className={styles.Body}>
                    <div className={styles.FieldGroup}>
                        <Input
                            type="text"
                            placeholder="PAN"
                            value={form.pan}
                            onChange={(value: string) => setField('pan', value)}
                            errorMessage={errors.pan}
                            isValid={!errors.pan}
                            containerClassName={styles.FieldContainer}
                            inputClassName={styles.FieldInput}
                            labelStyle={getLabelStyle('pan', form.pan)}
                            {...fieldFocusHandlers('pan')}
                        />
                        {/* The live region has to be rendered before the swap it announces, so it
                            wraps both states rather than sitting on the fetching label itself. */}
                        <div aria-live="polite">
                            {isIndividualPan(form.pan) && !panProfileFetching && (
                                <span className={styles.FromPanHint}>{PAN_PROFILE_COPY.FROM_PAN_HINT}</span>
                            )}
                            {panProfileFetching && (
                                <span className={styles.FetchingLabel}>
                                    <LoadingSpinner height="0.75rem" color="var(--primary-purple)" />
                                    {PAN_PROFILE_COPY.FETCHING}
                                </span>
                            )}
                        </div>
                    </div>

                    <div className={styles.FieldGroup}>
                        <div className={styles.PhoneRow}>
                            <CountryCodesDropdown
                                isModal
                                setCountryCode={setPhoneCountryCode}
                                // @ts-ignore — CountryCodesDropdown has no props interface; `style` is untyped there too (see InviteTeammateModal/AddAssessee usage)
                                style={{
                                    width: '6rem',
                                    height: '2.75rem',
                                    borderRadius: '0.25rem',
                                    border: '0.5px solid #D2D2D2',
                                    margin: 0,
                                }}
                            />
                            <Input
                                type="text"
                                placeholder="Phone No (optional)"
                                value={form.phone}
                                onChange={(value: string) => setField('phone', value)}
                                errorMessage={errors.phone}
                                isValid={!errors.phone}
                                maxLength={10}
                                containerClassName={styles.PhoneFieldContainer}
                                inputClassName={styles.FieldInput}
                                labelStyle={getLabelStyle('phone', form.phone)}
                                {...fieldFocusHandlers('phone')}
                            />
                        </div>
                    </div>

                    <div className={styles.FieldGroup}>
                        <label className={styles.FieldLabel}>Designation (optional)</label>
                        <div className={styles.DesignationField}>
                            <CircularSelect
                                option={[...DIRECTOR_DESIGNATIONS]}
                                selectedOption={form.designation}
                                placeholder="Select Designation"
                                addNewText="Add Other"
                                otherOptionPlaceholderText="Enter Designation..."
                                selectClassName={styles.DesignationSelect}
                                onSelect={(value: string) => {
                                    setField('designation', value);
                                    return Promise.resolve(true);
                                }}
                            />
                        </div>
                    </div>
                </div>

                <div className={styles.Footer}>
                    <Button
                        text="Add"
                        variant="primary"
                        size="wide"
                        onClick={handleSave}
                        loading={submitBlocked}
                        disabled={submitBlocked}
                        className={styles.SaveButton}
                    />
                    <div className={styles.FooterNote}>Note: You cannot change PAN after submission</div>
                </div>
            </div>
        </Modal>
    );
}

export default AddDirectorModal;
